import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, take } from 'rxjs/operators';

/* eslint-disable @typescript-eslint/naming-convention */
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    NgZone,
    OnInit
} from '@angular/core';
import { CookieConsentService } from '@core/services/cookie-consent/cookie-consent.service';
import { IntersectionService } from '@core/services/intersection.service';
import { LoggingService } from '@core/services/logging.service';
import { RelewiseTypes } from '@core/services/relewise/relewise.config';
import { RelewiseService } from '@core/services/relewise/relewise.service';
import { ProductSearchViewModelIEnumerableRecommendationResponse as relewiseResponse } from '@models/api-search.models';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ImpressionTypePipe } from '@shared/tracking/pipes/impression-type.pipe';
import { TranslateSafePipe } from '@shared/translate-safe/translate-safe.pipe';
import { ProductCardComponent } from '../../product/product-card/product-card.component';
import { ImpressionTrackingDirective } from '@shared/tracking/impression-tracking/impression-tracking.directive';
import { SliderComponent } from '../slider/slider.component';
import { SvgIconComponent } from '@shared/svg-icon/svg-icon.component';
import { LoadingDotsComponent } from '@shared/loading-dots/loading-dots.component';
import { AsyncPipe, NgClass, NgFor, NgIf, NgStyle } from '@angular/common';

@UntilDestroy()
@Component({
    selector: 'app-relewise-slider',
    templateUrl: './relewise-slider.component.html',
    styleUrls: ['./relewise-slider.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgClass,
        LoadingDotsComponent,
        NgIf,
        SvgIconComponent,
        NgStyle,
        SliderComponent,
        NgFor,
        ImpressionTrackingDirective,
        ProductCardComponent,
        AsyncPipe,
        TranslateSafePipe,
        ImpressionTypePipe
    ]
})
export class RelewiseSliderComponent implements OnInit {
    private intersectionKey = 'intersectionRelewiseSlider';
    public inView = false;
    public relewiseResponse: Observable<relewiseResponse | null> = new BehaviorSubject(null).pipe(
        mergeMap(() => this.cookieConsentService.consentStatus$),
        mergeMap(() => {
            const {
                LastSeen,
                MostPopular,
                MostPopularByBrand,
                MostPopularByCategory,
                ByProductId,
                Campaign,
                OtherAlsoBoughtProducts,
                OtherAlsoSeenProducts
            } = RelewiseTypes;

            if (this.type === LastSeen) {
                return (
                    this.relewiseService
                        .getLastSeen()
                        // If a productId is supplied, we filter that product from the result
                        .pipe(
                            map(res => ({
                                data: this.productId
                                    ? res.data?.filter(product => product.productNo !== this.productId)
                                    : res.data,
                                ...(res.temporaryId ? { temporaryId: res.temporaryId } : {})
                            }))
                        )
                );
            }

            if (this.type === MostPopular) {
                return this.relewiseService.getMostPopular();
            }

            if (this.type === MostPopularByBrand && this.brandId) {
                return this.relewiseService.getMostPopularByBrand(this.brandId);
            }

            if (this.type === MostPopularByCategory && this.categoryId) {
                return this.relewiseService.getMostPopularByCategory(this.categoryId);
            }

            if (this.type === OtherAlsoBoughtProducts && this.productId) {
                return this.relewiseService
                    .getOthersHaveDoneWith(this.productId)
                    .pipe(map(({ data }) => ({ data: data?.otherAlsoBoughtProducts || [] })));
            }
            if (this.type === OtherAlsoSeenProducts && this.productId) {
                return this.relewiseService
                    .getOthersHaveDoneWith(this.productId)
                    .pipe(map(({ data }) => ({ data: data?.otherAlsoSeenProducts || [] })));
            }

            if (this.type === ByProductId && this.productIdList?.length) {
                return this.relewiseService
                    .getProductsById(this.productIdList)
                    .pipe(map(res => ({ data: res.products })));
            }

            if (this.type === Campaign && this.campaignId) {
                return (
                    this.relewiseService
                        .getCampaign(this.campaignId)
                        // If a productId is supplied, we filter that product from the result,
                        // as we do not want to show the PDP product in '_Other_ products in campaign' slider.
                        .pipe(
                            map(res => ({
                                data: this.productId ? res.filter(product => product.productNo !== this.productId) : res
                            }))
                        )
                );
            }

            console.warn(`
                Could not render current Relewise type: ${this.type} based on provided data,
                check provided input and try again
            `);

            return of({ data: [] });
        }),
        untilDestroyed(this),
        catchError(err => {
            this.loggingService.logMessage('Relewise', `${this.type} failed: ${err}`);
            return of({ data: [] });
        })
    );

    @Input()
    public readonly type: RelewiseTypes;

    @Input()
    public readonly brandId?: number;

    @Input()
    public readonly categoryId?: number;

    @Input()
    public readonly campaignId?: string;

    @Input()
    public readonly productId?: string;

    @Input()
    public readonly productIdList?: string[];

    @Input()
    public readonly headline?: string;

    @Input()
    public readonly compressed?: boolean;

    @Input()
    public readonly centerAlign?: boolean;

    constructor(
        private relewiseService: RelewiseService,
        private intersectionService: IntersectionService,
        private element: ElementRef,
        private cd: ChangeDetectorRef,
        private loggingService: LoggingService,
        private cookieConsentService: CookieConsentService,
        private zone: NgZone
    ) {}

    ngOnInit(): void {
        this.intersectionService
            .observe(this.intersectionKey, this.element.nativeElement, { rootMargin: '100px' })
            .pipe(
                untilDestroyed(this),
                filter(
                    (entry: IntersectionObserverEntry) =>
                        entry.target === this.element.nativeElement && entry.isIntersecting
                ),
                take(1)
            )
            .subscribe(() => {
                this.inView = true;
                this.zone.run(() => {
                    this.cd.markForCheck();
                });
            });
    }
}
